/**
 * A list read a page at a time by keyset cursor, accumulated in the browser —
 * slice 52, gap 4, and the Meta ad drafts panel is its first caller.
 *
 * ## WHY THE PAGES ARE KEPT AND NOT THE ROWS
 *
 * "Show more" appends, so the obvious shape is one growing array of rows. It
 * is the wrong one, and the reason is what `useAsyncData` does: data already
 * on screen survives a refetch, so while the second page is in flight the
 * hook still answers with the first page's rows. An append written against
 * that answer appends the first page to itself, and the panel draws every row
 * on it twice — which looks exactly like the cursor bug this paging exists to
 * avoid, and would be blamed on the server.
 *
 * So the state is the pages, each under the cursor it was asked for, and
 * {@link keysetReceive} is the only way a page enters it. Receiving a page for
 * a cursor already held replaces it and drops everything after it — which is
 * what a retry of one page means — and receiving a page for a cursor not held
 * appends. Rows are the concatenation, computed, never stored. Nothing can
 * append twice, because a page is identified by where it came from rather than
 * by when it arrived.
 *
 * ## AND THE ANSWER CARRIES THE CURSOR IT WAS READ FOR
 *
 * Holding the pages under their cursors is not by itself enough, because the
 * cursor moves one render before the request does. "Show more" sets the
 * cursor; that render still has the *previous* page's answer on hand, ready
 * and non-null, and an accumulator that reads only `status === 'ready'` takes
 * it as this cursor's answer and files page one under page two's cursor. Every
 * row of page one is then drawn twice for as long as page two is in flight —
 * and for ever if page two fails, because nothing after a failure replaces it.
 *
 * The answer therefore carries the cursor it was read for, and
 * {@link keysetAccepts} takes it only when that cursor is the one being read.
 *
 * **And the query it was read for.** The cursor alone does not tell two first
 * pages apart: every query's first page is read at cursor `null`. When the
 * search changes, the reset below drops the pages and re-renders at once with
 * the new key — and the answer on hand is still the *old* query's first page,
 * settled, at cursor `null`. Taken, it is filed as the new query's first page
 * until the effect re-reads. Review found the visible case on the Reach page
 * (slice 65): clear a search that matched nothing and, for that window, an
 * empty answer to "zzz" was accepted as the unfiltered list, so a workspace
 * with drafts was told "No Meta ads yet". So the answer carries the
 * `resetKey` it was read under too, and an answer of another query is never
 * taken, whatever its cursor.
 * The decision is a pure function rather than three lines inside the hook so
 * that the suite can drive the in-flight render — a page-2 request over a
 * page-1 answer — without a browser, which is the only state this defect is
 * visible in.
 *
 * ## WHAT IT DOES NOT DO, AND WHY
 *
 * It does not de-duplicate rows by id. A duplicate row across a page boundary
 * is a defect in the cursor — two drafts sharing `updated_at` to the
 * millisecond with the id left out of the key is the classic one — and a
 * browser quietly dropping the repeat would hide it: the panel would look
 * right while the row after the boundary was never listed at all. The probe in
 * `verify-campaign-screens` drives exactly that fixture and asserts the rows
 * come through whole, which only works because nothing here launders them.
 *
 * It also does not go backwards. The contract answers `nextCursor` alone, so
 * there is no cursor for the previous page to hold; a Previous button would
 * need a stack of them and a count of pages nobody asked for. The list is
 * either continued or complete — see the panel.
 *
 * ## THE RESET IS A RENDER-PHASE ADJUSTMENT, ON PURPOSE
 *
 * When the search or the filter changes, every page held is a page of the old
 * query and must go *before* anything is drawn. Done in an effect, the old
 * rows paint once under the new search — the shape of every "it showed the
 * previous result for a frame" bug. `resetKey` is compared during render and
 * the state is replaced there, which is React's own documented way to derive
 * state from a prop that changed. Rejected: keying the whole panel by the
 * query string, which would also throw away the scroll position and the
 * loading state on every keystroke of a debounced search.
 */
import { useCallback, useState, type DependencyList } from 'react';

import { useAsyncData, type AsyncData, type AsyncStatus } from './use-async-data';

/** One page as the server answered it, under the cursor it was asked for. */
interface KeysetPage<T> {
  /** `null` is the first page — the read made with no cursor. */
  readonly cursor: string | null;
  readonly rows: readonly T[];
  /** Where the page after this one starts; `null` when this page ends the list. */
  readonly nextCursor: string | null;
}

export interface KeysetPages<T> {
  /** What the pages held were read for. See the header. */
  readonly key: string;
  readonly pages: readonly KeysetPage<T>[];
}

/** What one page of any keyset-paged list answers with. */
export interface KeysetAnswer<T> {
  readonly rows: readonly T[];
  readonly nextCursor: string | null;
}

/**
 * An answer with the cursor and the query it was read for. What the hook's own
 * read resolves to, so that {@link keysetAccepts} can tell this cursor's and
 * this query's answer from one still on hand from before. See the header.
 */
export interface KeysetAnswerAt<T> extends KeysetAnswer<T> {
  readonly cursor: string | null;
  /** The `resetKey` the read was made under. */
  readonly key: string;
}

/** No pages read yet, for the query `key` names. */
export function keysetStart<T>(key: string): KeysetPages<T> {
  return { key, pages: [] };
}

/**
 * The page read at `cursor`, taken into the state. See the header for why a
 * page already held is replaced rather than appended, and why everything after
 * it is dropped.
 */
export function keysetReceive<T>(
  state: KeysetPages<T>,
  cursor: string | null,
  answer: KeysetAnswer<T>,
): KeysetPages<T> {
  const page: KeysetPage<T> = { cursor, rows: answer.rows, nextCursor: answer.nextCursor };
  const at = state.pages.findIndex((held) => held.cursor === cursor);
  return {
    key: state.key,
    pages: at === -1 ? [...state.pages, page] : [...state.pages.slice(0, at), page],
  };
}

/** Every row read so far, in the order the pages came. */
export function keysetRows<T>(state: KeysetPages<T>): readonly T[] {
  return state.pages.flatMap((page) => page.rows);
}

/**
 * Where the next page starts, or `null` when the list is complete — and also
 * `null` before the first page has arrived, which is not the same thing and is
 * why a caller asks {@link keysetLoaded} before offering "Show more".
 */
export function keysetNextCursor<T>(state: KeysetPages<T>): string | null {
  return state.pages.at(-1)?.nextCursor ?? null;
}

/** Whether any page has arrived. */
export function keysetLoaded<T>(state: KeysetPages<T>): boolean {
  return state.pages.length > 0;
}

/**
 * The page to take into the state on this render, or `null` to take none.
 *
 * Every reason to decline is here, and each one is a defect somebody met:
 *
 *   - the read is loading or failed, so there is nothing settled to take;
 *   - the answer on hand was read for a different cursor — the render right
 *     after "Show more", which is what filed page one under page two's cursor
 *     and drew every row of it twice;
 *   - the search or the filter changed and this render is dropping the pages,
 *     so a page of the old query must not be filed under the new one;
 *   - the answer on hand was read for another query — the render right after
 *     the reset, when the state already carries the new key and the answer is
 *     still the old query's first page, at the same `null` cursor;
 *   - the page is already held, with the very rows on hand, so taking it again
 *     would be a `setState` per render for ever.
 *
 * Pure, and exported, because the render it decides — an answer for the
 * previous cursor while the next page is in flight — cannot be reached by
 * rendering the hook in a suite with no browser and no effects.
 */
export function keysetAccepts<T>(
  state: KeysetPages<T>,
  resetKey: string,
  cursor: string | null,
  read: { readonly status: AsyncStatus; readonly data: KeysetAnswerAt<T> | null },
): KeysetAnswerAt<T> | null {
  const answer = read.data;
  const settled =
    read.status === 'ready' && answer !== null && answer.cursor === cursor && answer.key === resetKey;
  if (!settled || state.key !== resetKey) return null;
  const held = state.pages.find((page) => page.cursor === cursor);
  return held === undefined || held.rows !== answer.rows ? answer : null;
}

export interface KeysetList<T> {
  /** Every row read so far. */
  readonly rows: readonly T[];
  /** Whether a further page can be asked for. */
  readonly hasMore: boolean;
  /** Asks for the next page. Does nothing when there is none, or one is in flight. */
  readonly more: () => void;
  /** Re-reads from the first page, dropping everything held. */
  readonly restart: () => void;
  /**
   * A page beyond the first failed, over rows already on screen — an
   * unreadable cursor, or the network. The rows stay, and the caller draws a
   * retry beside "Show more": moving the cursor cannot recover it, because the
   * cursor is already the one that failed, so {@link restart} is the way back.
   */
  readonly failedMore: boolean;
  /** The read itself, for its loading and error states. */
  readonly read: AsyncData<KeysetAnswerAt<T> | null>;
}

/**
 * A keyset-paged list, read page by page and accumulated.
 *
 * `resetKey` is everything the query is made of, as one string: when it
 * changes, the pages held are dropped during the same render. `load` is given
 * the cursor to read at — `null` for the first page — and answers `null` when
 * the list should not be read at all (a workspace with no ad account, say),
 * which is kept apart from an empty page.
 */
export function useKeysetPages<T>(
  resetKey: string,
  load: (cursor: string | null, signal: AbortSignal) => Promise<KeysetAnswer<T> | null>,
  deps: DependencyList,
): KeysetList<T> {
  const [state, setState] = useState<KeysetPages<T>>(() => keysetStart<T>(resetKey));
  const [cursor, setCursor] = useState<string | null>(null);

  /* The query changed: the pages held are of the old one. Replaced here rather
     than in an effect, so the old rows never paint under the new search. */
  if (state.key !== resetKey) {
    setState(keysetStart<T>(resetKey));
    setCursor(null);
  }

  /* The answer is tagged with the cursor and the query it was read for, here
     where both are known, so that `keysetAccepts` can decline the previous
     page's answer on the render the cursor moves, and the previous query's on
     the render the search changes. See the header. */
  const read = useAsyncData<KeysetAnswerAt<T> | null>(
    async (signal) => {
      const answer = await load(cursor, signal);
      return answer === null ? null : { ...answer, cursor, key: resetKey };
    },
    [...deps, resetKey, cursor],
  );

  /* Received during render rather than in an effect, for the same reason the
     reset is: a page that arrived is part of this render's list, not the next
     one's. Every reason to decline is `keysetAccepts`'s, and it is a page this
     cursor was read for or nothing. */
  const take = keysetAccepts(state, resetKey, cursor, read);
  if (take !== null) setState(keysetReceive(state, cursor, take));

  const next = keysetNextCursor(state);
  const loading = read.status === 'loading';
  const more = useCallback(() => {
    if (next !== null && !loading) setCursor(next);
  }, [next, loading]);
  /*
    Back to the first page, and read again — and the only way back from a read
    that failed, at whichever page it failed at.

    `reload` as well as the cursor, because the cursor alone recovers nothing.
    A first page that failed has no cursor to move. A LATER page that failed
    has one, but it is already the cursor that failed: moving it to itself
    changes no state and re-reads nothing, which is exactly how "Show more"
    came to be a button that did nothing at all after a refused page. And a
    cursor the API could not read is one it would refuse again, so the way back
    is the first page rather than another attempt at that one.
  */
  const { reload } = read;
  const restart = useCallback(() => {
    setCursor(null);
    setState((current) => keysetStart<T>(current.key));
    reload();
  }, [reload]);

  return {
    rows: keysetRows(state),
    hasMore: keysetLoaded(state) && next !== null,
    more,
    restart,
    /* Rows on screen and the read failed: the page that failed is a later one.
       A first page that failed leaves no pages held and is the caller's own
       "Not read" state instead. */
    failedMore: keysetLoaded(state) && read.status === 'error',
    read,
  };
}
